/**
 * `VoiceRecorder` — the standalone Capture voice tile.
 *
 * Composition:
 *   - `useVoiceCapture()`  — headless recording engine (agent/voice)
 *   - `<Waveform />`       — reused live+preview visualiser
 *   - `<VoiceConfirmationCard />` — post-parse tradie sanity check
 *
 * Screen state machine:
 *   idle → tap tile → recording (with live waveform)
 *       → tap tile → previewing (playback + re-record + send to Whisper)
 *       → send    → uploading (progress dots on Send button)
 *       → parsed  → confirming (VoiceConfirmationCard inside the sheet)
 *       → confirm → nav /review with the parsed shape
 *       → re-record from any state → back to recording
 *
 * TypeScript migration note: this file replaces the previous
 * `VoiceRecorder.jsx`. Public prop shape unchanged; consumers
 * (`Capture.jsx`) require no updates.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mic,
  Square,
  Radio,
  Play,
  Pause,
  RotateCcw,
  Send,
  UserRound,
} from "lucide-react";
import {
  Sheet,
  SheetContent as SheetContentJs,
  SheetHeader as SheetHeaderJs,
  SheetTitle as SheetTitleJs,
  SheetDescription as SheetDescriptionJs,
} from "@/components/ui/sheet";
import WaveformJs from "@/components/Waveform";
import { devLog } from "@/lib/log";
import { useVoiceCapture, type VoiceParseResult } from "@/agent/voice";
import { VoiceConfirmationCard } from "./VoiceConfirmationCard";

// Widen the JS Shadcn primitives at the seam — they're forwardRef'd and TS
// can't infer their prop shape via allowJs. Same pattern as History.tsx.
const SheetContent = SheetContentJs as unknown as React.FC<
  React.PropsWithChildren<{ side?: string; className?: string }>
>;
const SheetHeader = SheetHeaderJs as unknown as React.FC<
  React.PropsWithChildren<{ className?: string }>
>;
const SheetTitle = SheetTitleJs as unknown as React.FC<
  React.PropsWithChildren<{ className?: string }>
>;
const SheetDescription = SheetDescriptionJs as unknown as React.FC<
  React.PropsWithChildren<{ className?: string }>
>;
const Waveform = WaveformJs as unknown as React.FC<{
  stream?: MediaStream | null;
  audioEl?: HTMLAudioElement | null;
  active?: boolean;
  barCount?: number;
  accent?: string;
}>;

interface Props {
  activeJobId?: string | null;
  supplierSuggestions?: string[];
  disabled?: boolean;
}

function fmtTime(s: number): string {
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export default function VoiceRecorder({
  activeJobId,
  supplierSuggestions = [],
  disabled = false,
}: Props) {
  const nav = useNavigate();
  const {
    status,
    elapsedSec,
    liveStream,
    preview,
    error,
    start,
    stop,
    discard,
    upload,
  } = useVoiceCapture();

  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewPos, setPreviewPos] = useState(0);
  const [forcePersonal, setForcePersonal] = useState(false);
  const [confirming, setConfirming] = useState<VoiceParseResult | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Surface mic-permission errors from the headless hook exactly once.
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // Reset personal-toggle whenever the sheet closes for real.
  useEffect(() => {
    if (!preview && !confirming) setForcePersonal(false);
  }, [preview, confirming]);

  const isRecording = status === "recording";
  const isUploading = status === "uploading";
  const sheetOpen = Boolean(preview || confirming);
  const btnDisabled = disabled || isUploading;

  const togglePreviewPlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (previewPlaying) a.pause();
    else a.play().catch(() => {});
  };

  const reRecord = useCallback(() => {
    setConfirming(null);
    setPreviewPlaying(false);
    setPreviewPos(0);
    discard();
    // Fire-and-forget — start() handles its own permission errors via `error`.
    void start();
  }, [discard, start]);

  const closeSheet = useCallback(() => {
    setConfirming(null);
    setPreviewPlaying(false);
    setPreviewPos(0);
    discard();
  }, [discard]);

  /**
   * Applied to the parsed shape RIGHT BEFORE navigation. Ensures that:
   *   - `job_id` falls back to the tradie's current active job
   *   - forcePersonal toggle synthesises a `personal_suggestion` for the
   *     Review screen's PersonalExpenseToggle when the AI heuristic didn't
   *     fire on its own
   */
  const decorateForReview = useCallback(
    (parsed: VoiceParseResult): VoiceParseResult => {
      const withJob = {
        ...parsed,
        job_id: (parsed as VoiceParseResult & { job_id?: string }).job_id || activeJobId || undefined,
      } as VoiceParseResult;
      if (forcePersonal && !withJob.personal_suggestion) {
        withJob.personal_suggestion = {
          top_kind: "deduction",
          sub_category: "other",
          reason: "You marked this as personal",
        };
      }
      return withJob;
    },
    [activeJobId, forcePersonal]
  );

  const sendPreviewToTranscribe = useCallback(async () => {
    if (!preview) return;
    try {
      const parsed = await upload();
      // Land on the in-sheet Confirmation card. From here the tradie either
      // confirms (nav /review), edits (nav /review — same destination, the
      // ceremony is what matters), or re-records.
      setConfirming(decorateForReview(parsed));
    } catch (err) {
      devLog.warn("transcribe-voice failed", err);
      toast.error("Voice parse failed. You can still enter manually.");
      const fallback = decorateForReview({
        supplier: "",
        description: "",
        total: 0,
        gst: 0,
        entry_date: "",
        deduction_category: "materials",
        notes: "",
        confirmation_line: "",
        confidence: {},
        raw_transcript: "",
        source: "voice",
      } as VoiceParseResult);
      closeSheet();
      nav("/review", {
        state: { parsed: fallback, source: "voice", supplierSuggestions },
      });
    }
  }, [preview, upload, decorateForReview, nav, supplierSuggestions, closeSheet]);

  const confirmNavigate = useCallback(() => {
    if (!confirming) return;
    const parsed = confirming;
    setConfirming(null);
    nav("/review", {
      state: { parsed, source: "voice", supplierSuggestions },
    });
  }, [confirming, nav, supplierSuggestions]);

  return (
    <>
      <button
        data-testid="voice-note-btn"
        onClick={isRecording ? stop : () => void start()}
        disabled={btnDisabled}
        className={`group h-20 w-full rounded-2xl flex items-center px-4 gap-4 relative overflow-hidden ${
          isRecording
            ? "bg-[#F87171] text-[#111827] shadow-[0_10px_30px_-12px_rgba(248,113,113,0.7)]"
            : "bi-elevate hover:brightness-[1.08]"
        } active:scale-[0.99] transition disabled:opacity-70`}
      >
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center transition ${
            isRecording
              ? "bg-[#111827]/15 text-[#111827]"
              : "bg-[#FF9500]/15 text-[#FF9500] group-hover:bg-[#FF9500]/25"
          }`}
        >
          {isRecording ? (
            <Square className="w-5 h-5" fill="currentColor" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="font-heading font-semibold text-[17px] leading-tight">
            {isRecording ? `Recording · ${fmtTime(elapsedSec)}` : "Voice Note"}
          </div>
          <div
            className={`text-[13px] leading-snug ${isRecording ? "text-[#111827]/80" : "text-white/60"}`}
          >
            {isRecording
              ? "Tap to stop and preview."
              : "Speak the expense — check figures before save."}
          </div>
        </div>
        {isRecording && <Radio className="w-5 h-5 text-[#111827] animate-pulse" />}
        {isRecording && (
          <div
            className="absolute inset-x-4 bottom-1 h-7 pointer-events-none opacity-90"
            data-testid="voice-live-waveform"
          >
            <Waveform stream={liveStream} active accent="#111827" barCount={36} />
          </div>
        )}
      </button>

      <Sheet
        open={sheetOpen}
        onOpenChange={(o) => {
          if (!o) closeSheet();
        }}
      >
        <SheetContent
          side="bottom"
          className="bg-[#1F1F1F] border-white/10 text-white p-0"
        >
          <SheetHeader className="px-5 pt-5 pb-2 text-left">
            <SheetTitle className="font-heading font-extrabold text-xl">
              {confirming ? "Does this look right?" : "Preview your voice note"}
            </SheetTitle>
            <SheetDescription className="text-white/60 text-sm">
              {confirming
                ? "Confirm to open the full review, or re-record if we missed the mark."
                : "Have a listen — re-record if it's not quite right, or send it for transcription."}
            </SheetDescription>
          </SheetHeader>
          <div className="px-5 pt-4 pb-6 space-y-4">
            {preview?.url && (
              <audio
                ref={audioRef}
                src={preview.url}
                onPlay={() => setPreviewPlaying(true)}
                onPause={() => setPreviewPlaying(false)}
                onTimeUpdate={(e) =>
                  setPreviewPos(e.currentTarget.currentTime || 0)
                }
                onEnded={() => {
                  setPreviewPlaying(false);
                  setPreviewPos(0);
                }}
              />
            )}

            {/* PRE-PARSE preview: play / re-record / send-to-Whisper */}
            {!confirming && (
              <>
                <div className="bi-surface p-4 flex items-center gap-4">
                  <button
                    type="button"
                    data-testid="voice-preview-play"
                    onClick={togglePreviewPlay}
                    className="w-14 h-14 rounded-full bi-accent-btn flex items-center justify-center shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)] shrink-0"
                  >
                    {previewPlaying ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6 ml-0.5" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-white/50 font-heading">
                      Recording
                    </div>
                    <div className="mt-1 font-heading font-extrabold text-2xl tabular-nums">
                      {fmtTime(Math.round(previewPos))}{" "}
                      <span className="text-white/40 text-base font-normal">
                        / {fmtTime(preview?.duration || 0)}
                      </span>
                    </div>
                    <div
                      className="mt-2 h-10"
                      data-testid="voice-preview-waveform"
                    >
                      <Waveform
                        audioEl={audioRef.current}
                        active={previewPlaying}
                        accent="#FF9500"
                        barCount={36}
                      />
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-[#FF9500] transition-[width] duration-100"
                        style={{
                          width: `${
                            preview?.duration
                              ? Math.min(
                                  100,
                                  (previewPos / preview.duration) * 100
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="voice-preview-personal"
                  onClick={() => setForcePersonal((v) => !v)}
                  className={`w-full h-11 rounded-xl inline-flex items-center justify-center gap-2 text-xs font-heading font-bold uppercase tracking-wider transition ${
                    forcePersonal
                      ? "bg-[#FF9500]/20 text-[#FF9500] border border-[#FF9500]/40"
                      : "bi-ghost-btn text-white/60"
                  }`}
                >
                  <UserRound className="w-4 h-4" />
                  {forcePersonal
                    ? "Personal · will open in Review"
                    : "Mark as personal expense"}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="voice-preview-rerecord"
                    onClick={reRecord}
                    disabled={isUploading}
                    className="h-14 rounded-xl bi-ghost-btn flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4" /> Re-record
                  </button>
                  <button
                    type="button"
                    data-testid="voice-preview-send"
                    onClick={sendPreviewToTranscribe}
                    disabled={isUploading}
                    className="h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
                  >
                    {isUploading ? (
                      <>
                        <div className="w-2 h-2 bg-[#111827] rounded-full animate-pulse" />{" "}
                        Transcribing…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Send for transcription
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-white/40 text-center">
                  Audio never leaves your device until you tap Send.
                </p>
              </>
            )}

            {/* POST-PARSE confirmation */}
            {confirming && (
              <VoiceConfirmationCard
                parsed={confirming}
                onConfirm={confirmNavigate}
                onEdit={confirmNavigate}
                onRerecord={reRecord}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
